import dayjs from "dayjs";
import "dayjs/locale/ru";
import { useEffect } from "react";
import { useDispatch } from "react-redux";

import { getEventsByDatesAPI } from "../api/eventAPI";
import { eventsMerged, monthsFailed, monthsLoading } from "./events";

// Load a little past the visible edges, rounded out to whole months, so stepping the view
// usually finds its events already loaded.
const PREFETCH_DAYS = 14;
const MAX_RETRIES = 3;
// Months shown by the half-year, year and quarter layouts (indexes as in CalendarApp's layouts).
const PERIOD_MONTHS = { 3: 6, 4: 12, 5: 3 };

const monthStart = (month) => dayjs(`${month}-01`);

export const monthsInRange = (from, to) => {
    const months = [];
    const last = dayjs(to).startOf('month');
    for (let month = dayjs(from).startOf('month'); !month.isAfter(last); month = month.add(1, 'month')) {
        months.push(month.format('YYYY-MM'));
    }

    return months;
}

// Splits ascending month keys into runs of consecutive months; each run is one request.
export const consecutiveRuns = (months) => months.reduce((runs, month) => {
    const run = runs[runs.length - 1];
    if (run && monthStart(run[run.length - 1]).add(1, 'month').format('YYYY-MM') === month) {
        run.push(month);
    } else {
        runs.push([month]);
    }

    return runs;
}, []);

// First and last day ('YYYY-MM-DD') a layout can show for `date`. Month-based grids also show
// neighbouring weeks, and the skeuomorphic week ends on the next Monday.
export const visibleRange = (layout, date) => {
    const day = dayjs(date).locale('ru');
    let from = day;
    let to = day;

    if (layout === 1) {
        from = day.startOf('week');
        to = from.add(1, 'week');
    } else if (layout === 2) {
        from = day.startOf('month').subtract(1, 'week');
        to = day.endOf('month').add(1, 'week');
    } else if (PERIOD_MONTHS[layout]) {
        from = day.startOf('month').subtract(1, 'week');
        to = day.add(PERIOD_MONTHS[layout], 'month').add(1, 'week');
    }

    return [from.format('YYYY-MM-DD'), to.format('YYYY-MM-DD')];
}

// Fetches the months that are neither loaded nor in flight. Resolves to false if a request failed.
export const loadEventMonths = (months) => (dispatch, getState) => {
    const { loadedMonths, loadingMonths, generation } = getState().events;
    const missing = months.filter(month => !loadedMonths[month] && !loadingMonths[month]);
    if (missing.length === 0) {
        return Promise.resolve(true);
    }

    dispatch(monthsLoading(missing));

    return Promise.all(consecutiveRuns(missing).map(run => {
        const from = monthStart(run[0]);
        const to = monthStart(run[run.length - 1]).endOf('month');

        return getEventsByDatesAPI(from, to)
            .then(events => {
                // After a logout the response belongs to the previous user.
                if (getState().events.generation === generation) {
                    dispatch(eventsMerged({ events, months: run }));
                }
                return true;
            })
            .catch(err => {
                console.log(err);
                if (getState().events.generation === generation) {
                    dispatch(monthsFailed(run));
                }
                return false;
            });
    })).then(results => results.every(Boolean));
}

// Keeps the events of the visible range loaded, retrying failed months with backoff. No range, no loading.
export const useEventsRange = (from, to) => {
    const dispatch = useDispatch();

    useEffect(() => {
        if (!from || !to) return undefined;

        const months = monthsInRange(
            dayjs(from).subtract(PREFETCH_DAYS, 'day'),
            dayjs(to).add(PREFETCH_DAYS, 'day'),
        );
        let cancelled = false;
        let retryTimer = null;

        const load = (attempt) => {
            dispatch(loadEventMonths(months)).then(ok => {
                if (ok || cancelled || attempt >= MAX_RETRIES) return;
                retryTimer = setTimeout(() => load(attempt + 1), 1000 * 2 ** attempt);
            });
        };
        load(0);

        return () => {
            cancelled = true;
            clearTimeout(retryTimer);
        };
    }, [from, to, dispatch]);
}
